import { type QueryClient, useQuery } from '@tanstack/react-query';
import { api, ApiError, BASE_PATH, type Me } from './api.ts';

export const ME_KEY = ['auth', 'me'];

/** Quem está logado. `null` quando não há sessão. */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/** A pessoa tem a permissão? (para mostrar menus e botões) */
export function can(me: Me | null | undefined, permission: string): boolean {
  return !!me?.permissions.includes(permission);
}

export async function logout(): Promise<void> {
  await api.post<void>('/auth/logout');
  window.location.assign(`${BASE_PATH}/login`);
}

/**
 * Sigilo ao trocar de sessão ou de permissões (v3.1): os dados guardados na tela são descartados,
 * para nada calculado com outro perfil (por exemplo, com pessoal) continuar à vista. O servidor
 * confere as permissões a cada requisição; isto só evita mostrar o que já estava carregado.
 */
export function dropDataOnAccessChange(client: QueryClient) {
  let last: string | undefined;
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.query.queryKey[0] !== ME_KEY[0]) return;
    const me = event.query.state.data as Me | null | undefined;
    if (me === undefined) return;
    const signature = JSON.stringify(me ? [me.user.id, me.permissions] : null);
    if (last !== undefined && signature !== last) {
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
    }
    last = signature;
  });
}
